import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { Search, Check, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiGet } from '@/lib/api';
import { showToast } from './Toast';
import { useAuthStore } from '@/stores';
import { createGroupChat } from '@/services/groupChatService';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface CreateGroupChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  onGroupCreated: (
    conversationId: string,
    members: Array<{
      userId: number;
      role: 'owner' | 'manager' | 'editor' | 'viewer';
    }>
  ) => void;
}

export default function CreateGroupChatModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  onGroupCreated,
}: CreateGroupChatModalProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedUsersRef = useRef(false);

  // Get current user info
  const { userInfo } = useAuthStore();
  const currentUserId = userInfo?.id;

  // Fetch users from project members API
  const loadUsersFromAPI = useCallback(async () => {
    if (hasLoadedUsersRef.current) {
      return;
    }
    try {
      setIsLoading(true);
      // 🔥 改用项目成员 API，只显示项目中的成员
      const url = `/api/projects/${projectId}/users`;
      const response = await apiGet<any>(url);
      const data = response?.data || response || {};
      if (data && data.users) {
        // 转换 API 响应格式并排除当前登录用户
        const allUsers: User[] = data.users.map((item: any) => ({
          id: String(item.user.id),
          name: item.user.name,
          email: item.user.email,
        }));
        const filteredOutCurrentUser = currentUserId
          ? allUsers.filter((user: User) => {
              const userId = String(user.id);
              const currentId = String(currentUserId);
              return userId !== currentId;
            })
          : allUsers;
        setUsers(filteredOutCurrentUser);
        setFilteredUsers(filteredOutCurrentUser);
        hasLoadedUsersRef.current = true;
      } else {
        setUsers([]);
        setFilteredUsers([]);
        hasLoadedUsersRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching project members:', error);
      showToast(t('errors.failedToLoadProjectMembers'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, projectId, t]);

  // Filter users based on search query
  const filterUsers = useCallback(
    (search: string) => {
      const trimmedSearch = search.trim().toLowerCase();
      if (trimmedSearch) {
        setFilteredUsers(
          users.filter(
            user =>
              user.name.toLowerCase().includes(trimmedSearch) ||
              user.email.toLowerCase().includes(trimmedSearch)
          )
        );
      } else {
        setFilteredUsers(users);
      }
    },
    [users]
  );

  // Handle search with debounce
  useEffect(() => {
    if (!hasLoadedUsersRef.current) {
      return;
    }
    const debounceTimer = setTimeout(() => {
      filterUsers(searchQuery);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterUsers]);

  // Initial load when modal opens
  useEffect(() => {
    if (isOpen) {
      hasLoadedUsersRef.current = false;
      setSelectedUsers(new Set());
      setSearchQuery('');
      setTitle('');
      if (currentUserId) {
        loadUsersFromAPI();
      } else {
        setTimeout(() => {
          hasLoadedUsersRef.current = false;
          loadUsersFromAPI();
        }, 100);
      }
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, currentUserId, loadUsersFromAPI]);

  // Handle user selection
  const toggleUserSelection = useCallback((userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    const isAllSelected = filteredUsers.every(user =>
      selectedUsers.has(user.id)
    );
    if (isAllSelected) {
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        filteredUsers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        filteredUsers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers]);

  // Handle create group chat
  const handleCreate = async () => {
    if (!title.trim()) {
      showToast(t('groupChat.groupNameRequired'), 'warning');
      titleInputRef.current?.focus();
      return;
    }
    if (selectedUsers.size === 0) {
      showToast(t('groupChat.selectAtLeastOneMember'), 'warning');
      return;
    }

    try {
      setIsCreating(true);

      // Build users array with roles
      // Current user is the owner, selected users are editors (can participate in chat)
      const groupUsers: Array<{
        userId: number;
        role: 'owner' | 'manager' | 'editor' | 'viewer';
      }> = [];

      // Add current user as owner
      // if (currentUserId) {
      //   groupUsers.push({
      //     userId: Number(currentUserId),
      //     role: 'owner',
      //   });
      // }

      // Add selected users as editors (can participate in chat)
      selectedUsers.forEach(userId => {
        groupUsers.push({
          userId: Number(userId),
          role: 'editor',
        });
      });

      const response = await createGroupChat({
        users: groupUsers,
        projectId: projectId,
        title: title.trim(),
      });

      if (response.code === 200 && response?.conversationId) {
        showToast(t('toast.groupChatCreated'), 'success');
        onGroupCreated(response.conversationId, groupUsers);
        onClose();
      } else {
        showToast(
          response.message || t('toast.groupChatCreateFailed'),
          'error'
        );
      }
    } catch (error) {
      console.error('Error creating group chat:', error);
      showToast(t('toast.groupChatCreateFailed'), 'error');
    } finally {
      setIsCreating(false);
    }
  };

  // Get user avatar initials
  const getAvatarInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Generate avatar background color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-red-400',
      'bg-orange-400',
      'bg-yellow-400',
      'bg-green-400',
      'bg-blue-400',
      'bg-indigo-400',
      'bg-purple-400',
      'bg-pink-400',
      'bg-teal-400',
      'bg-cyan-400',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (!isOpen) return null;

  const isAllSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every(user => selectedUsers.has(user.id));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-4 pt-6 pb-3 border-b">
          <DialogTitle>{t('groupChat.startGroupChat')}</DialogTitle>
          <div className="pt-3 bg-gray-50 -mx-4 px-4 py-3 mt-3">
            <p className="text-sm text-gray-600 truncate" title={projectName}>
              {t('groupChat.projectLabel')}
              {projectName}
            </p>
          </div>
        </DialogHeader>

        {/* Title Input */}
        <div className="px-6 py-4 border-b">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('form.title')} <span className="text-red-500">*</span>
          </label>
          <input
            ref={titleInputRef}
            type="text"
            placeholder={t('form.enterChatName')}
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Search */}
        <div className="px-4 border-b">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('form.searchMembers')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-1 mb-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Select All */}
        {filteredUsers.length > 0 && (
          <div className="px-4 border-b">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-3 w-full text-left hover:bg-gray-50 p-2 rounded-md transition-colors"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                {isAllSelected && <Check size={16} className="text-blue-600" />}
                {!isAllSelected && (
                  <Users size={16} className="text-blue-600" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {t('share.all')}
                </div>
              </div>
              <div className="w-4 h-4 border-2 border-gray-300 rounded flex items-center justify-center">
                {isAllSelected && <Check size={12} className="text-blue-600" />}
              </div>
            </button>
          </div>
        )}

        {/* Users list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-center text-gray-500">
              {t('groupChat.loadingProjectMembers')}
            </div>
          ) : users.length === 0 ? (
            // 🔥 项目中没有其他成员时显示提示信息
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <Users size={24} className="text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                {t('form.noOtherMembers')}
              </p>
              <p className="text-xs text-gray-500">
                {t('form.addMembersFirst')}
              </p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              {t('groupChat.noMatchingMembers')}
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => toggleUserSelection(user.id)}
                  className="flex items-center gap-3 pb-2 border-b w-full text-left hover:bg-gray-50 px-3 rounded-md transition-colors"
                >
                  {/* Avatar */}
                  <div
                    className={cn(
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                      getAvatarColor(user.name)
                    )}
                  >
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getAvatarInitials(user.name)
                    )}
                  </div>
                  {/* User info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {user.email}
                    </div>
                  </div>
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                      selectedUsers.has(user.id)
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {selectedUsers.has(user.id) && (
                      <Check size={12} className="text-white" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedUsers.size === 1
              ? t('groupChat.memberSelected')
              : t('groupChat.membersSelected', { count: selectedUsers.size })}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isCreating}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating || selectedUsers.size === 0 || !title.trim()}
            >
              {isCreating ? t('groupChat.creating') : t('common.ok')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
